// src/pages/finance-portal/tax-audit-trail/TaxAuditTrail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    CircularProgress,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const ACTION_COLORS = {
    CREATE: { bg: '#dcfce7', color: '#166534' },
    UPDATE: { bg: '#dbeafe', color: '#1e40af' },
    DELETE: { bg: '#fecaca', color: '#991b1b' },
    VERIFY: { bg: '#ede9fe', color: '#6d28d9' },
    CALCULATE: { bg: '#fef3c7', color: '#92400e' },
    DISPOSE: { bg: '#fed7aa', color: '#9a3412' },
    FILE: { bg: '#cffafe', color: '#155e75' },
    REVIEW: { bg: '#ede9fe', color: '#6d28d9' },
    GENERATE: { bg: '#d1fae5', color: '#065f46' },
};

const ENTITY_TYPES = [
    { value: '', label: 'All Entity Types' },
    { value: 'tax_exemption', label: 'Tax Exemption' },
    { value: 'tax_adjustment', label: 'Tax Adjustment' },
    { value: 'estimated_payment', label: 'Estimated Payment' },
    { value: 'depreciation_asset', label: 'Depreciation Asset' },
    { value: 'tax_document', label: 'Tax Document' },
    { value: 'tax_return_filing', label: 'Tax Return Filing' },
    { value: 'tax_planning_scenario', label: 'Tax Planning Scenario' },
];

const ACTIONS = [
    { value: '', label: 'All Actions' },
    { value: 'CREATE', label: 'Create' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'DELETE', label: 'Delete' },
    { value: 'VERIFY', label: 'Verify' },
    { value: 'CALCULATE', label: 'Calculate' },
    { value: 'DISPOSE', label: 'Dispose' },
    { value: 'FILE', label: 'File' },
    { value: 'REVIEW', label: 'Review' },
    { value: 'GENERATE', label: 'Generate' },
];

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatTimestamp(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function TaxAuditTrail() {
    const { enqueueSnackbar } = useSnackbar();
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [filterAction, setFilterAction] = useState('');
    const [filterEntityType, setFilterEntityType] = useState('');
    const [filterPerformedBy, setFilterPerformedBy] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Entity lookup
    const [lookupEntityType, setLookupEntityType] = useState('');
    const [lookupEntityId, setLookupEntityId] = useState('');
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);

    const [exporting, setExporting] = useState(false);

    const fetchEntries = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (filterAction) params.action = filterAction;
            if (filterEntityType) params.entity_type = filterEntityType;
            if (filterPerformedBy) params.performed_by = filterPerformedBy;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            const res = await axiosInstance.get('/api/finance/tax/audit-trail', { params });
            if (res.data.success) {
                setEntries(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load audit trail');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, filterAction, filterEntityType, filterPerformedBy, startDate, endDate]);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            const res = await axiosInstance.get('/api/finance/tax/audit-trail/export', {
                params,
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `tax-audit-trail-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            enqueueSnackbar('Export downloaded', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Export failed', { variant: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const handleEntityLookup = async () => {
        if (!lookupEntityType || !lookupEntityId) return;
        setLookupLoading(true);
        setLookupResult(null);
        try {
            const res = await axiosInstance.get('/api/finance/tax/audit-trail/entity', {
                params: { entity_type: lookupEntityType, entity_id: lookupEntityId },
            });
            if (res.data.success) {
                setLookupResult(res.data.data || []);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Lookup failed', { variant: 'error' });
        } finally {
            setLookupLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <HistoryIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Audit Trail</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Immutable log of all tax-related actions</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={exporting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={exporting}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Export
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Entity Lookup */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3, p: 2.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 1.5 }}>Entity Lookup</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small" select label="Entity Type" value={lookupEntityType}
                            onChange={e => setLookupEntityType(e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            sx={{ minWidth: 180 }}
                        >
                            {ENTITY_TYPES.filter(e => e.value).map(e => (
                                <MenuItem key={e.value} value={e.value} sx={{ fontFamily }}>{e.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small" label="Entity ID" value={lookupEntityId}
                            onChange={e => setLookupEntityId(e.target.value)}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            sx={{ minWidth: 120 }}
                        />
                        <Button
                            variant="contained" size="small"
                            startIcon={lookupLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SearchIcon />}
                            onClick={handleEntityLookup}
                            disabled={lookupLoading || !lookupEntityType || !lookupEntityId}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, height: 40,
                            }}
                        >
                            Lookup
                        </Button>
                    </Box>
                    {lookupResult && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 1 }}>
                                Found {lookupResult.length} record(s) for {lookupEntityType} #{lookupEntityId}
                            </Typography>
                            {lookupResult.length > 0 && (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {['Timestamp', 'Action', 'Description', 'Performed By'].map(h => (
                                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {lookupResult.map((entry, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={bodyCellSx}>{formatTimestamp(entry.created_at || entry.timestamp)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        <Chip label={entry.action} size="small"
                                                            sx={{
                                                                fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                                bgcolor: (ACTION_COLORS[entry.action] || ACTION_COLORS.CREATE).bg,
                                                                color: (ACTION_COLORS[entry.action] || ACTION_COLORS.CREATE).color,
                                                            }} />
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>{entry.description || '\u2014'}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{entry.performed_by || '\u2014'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </Card>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" select label="Action" value={filterAction}
                        onChange={e => { setFilterAction(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 140 }}
                    >
                        {ACTIONS.map(a => <MenuItem key={a.value} value={a.value} sx={{ fontFamily }}>{a.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" select label="Entity Type" value={filterEntityType}
                        onChange={e => { setFilterEntityType(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 180 }}
                    >
                        {ENTITY_TYPES.map(e => <MenuItem key={e.value} value={e.value} sx={{ fontFamily }}>{e.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" label="Performed By" value={filterPerformedBy}
                        onChange={e => { setFilterPerformedBy(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 150 }}
                        placeholder="User name or ID"
                    />
                    <TextField
                        size="small" label="Start Date" type="date" value={startDate}
                        onChange={e => { setStartDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: 150 }}
                    />
                    <TextField
                        size="small" label="End Date" type="date" value={endDate}
                        onChange={e => { setEndDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: 150 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Timestamp', 'Action', 'Entity Type', 'Entity ID', 'Description', 'Performed By'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : entries.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No audit trail entries found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : entries.map(entry => (
                                    <TableRow key={entry.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={bodyCellSx}>{formatTimestamp(entry.created_at || entry.timestamp)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={entry.action} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (ACTION_COLORS[entry.action] || ACTION_COLORS.CREATE).bg,
                                                    color: (ACTION_COLORS[entry.action] || ACTION_COLORS.CREATE).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{getStatusLabel(entry.entity_type)}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{entry.entity_id || '\u2014'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{entry.description || '\u2014'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{entry.performed_by || '\u2014'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>
        </Box>
    );
}
